import React, { useCallback, useState } from "react";
import { App } from "antd5";
import { Redirect } from "wouter";

import { DocumentSlideout } from "components/bid_module/qualification_report_section/DocumentSlideout";
import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { AI_CONVERSATION, useVariableValue } from "lib/featureFlags";
import { useGetBidDocumentUrl } from "lib/hooks/api/bid/bid_document/useGetBidDocumentUrl";
import { useBid } from "lib/hooks/api/bid/bids/useBid";
import { getBidDocumentUrlForExternalUser } from "lib/hooks/api/bid/public/getBidDocumentUrlForExternalUser";
import { useGetOrQualifyBid } from "lib/hooks/api/bid/scorecard/useGetOrQualifyBid";
import { useQualifyBid } from "lib/hooks/api/bid/scorecard/useQualifyBid";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { AiChatStreamResponse_Sources } from "../../../lib/generated/app-service-gql/graphql";
import { Flex, Text } from "../../../styles/utility-components";
import { DetailsContent } from "../../app_layout/DetailsLayout";
import DocumentDrawer from "../DocumentViewer";
import { Scorecard_Source } from "../types";
import AskStotlesAiModal from "./ask_question_modal/AskStotlesAiModal";
import StotlesAiModal from "./ask_question_modal/StotlesAiModal";
import { BidNoBidReportHeader } from "./BidNoBidReportHeader";
import { BidNoBidReportSection } from "./BidNoBidReportSection";
import BidReportLoadingState from "./BidReportLoadingState";
import { NOTICE_SOURCE } from "./DocumentSources";

import css from "./BidNoBidReport.module.scss";

type Props = {
  bidId: string;

  // TODO: Make this string non-optional once we get rid of the
  // notice urls on opportunities - should be very soon
  opportunityId?: string;
};
export function BidNoBidReport({ bidId, opportunityId }: Props) {
  const { logEvent } = useTracking();
  const { message } = App.useApp();

  const useAiConversationStream = useVariableValue(AI_CONVERSATION, false);

  const [documentData, setDocumentData] = useState<Scorecard_Source | null>(null);
  // Documents slideout open by default
  const [slideoutOpen, setSlideoutOpen] = useState(true);
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);

  const [debug, setDebug] = useState(false);
  const [isAskAiModalOpen, setIsAskAiModalOpen] = useState(false);

  const { data: bidInfo, isLoading: isLoadingBidInfo } = useBid(bidId, opportunityId);

  const opportunityName = bidInfo?.opportunity.name || "";
  const noticeId = bidInfo?.notice?.guid;
  const noticeTitle = bidInfo?.notice?.name ?? undefined;
  const buyerName = bidInfo?.notice?.buyers?.[0]?.name;
  const buyerId = bidInfo?.notice?.buyers?.[0]?.guid;
  const procurementStageId = bidInfo?.procurementStageId;
  const urlShareSignature = bidInfo?.publicHash ?? null;

  const { data, isLoading: isQuerying } = useGetOrQualifyBid(bidId);

  const { mutate: qualifyBidReport, isLoading: isQualifying } = useQualifyBid({
    onError: () => {
      message.error("Failed to create bid report");
    },
  });

  const onRegenerateBidReport = useCallback(
    async (eventName: EventNames) => {
      logEvent(eventName, {
        "Action type": "Button clicked",
      });
      qualifyBidReport({
        id: bidId,
      });
    },
    [bidId, logEvent, qualifyBidReport],
  );

  const { mutateAsync: getBidDocumentUrlMutation } = useGetBidDocumentUrl();

  const { viewRecord } = useRestricedViewNotice("Title click");

  const handleNoticeLink = useCallback(() => {
    if (window.currentUser) {
      if (noticeId) {
        viewRecord(noticeId);
      }
    } else {
      // Open in a new tab
      window.open(`${routes.records}/${noticeId}`, "_blank");
    }
  }, [noticeId, viewRecord]);

  const handleDocumentLink = useCallback(
    async (source: AiChatStreamResponse_Sources) => {
      if (!source.documentId) {
        return;
      }

      let documentUrl = "";
      if (window.currentUser) {
        const { getBidDocumentUrl } = await getBidDocumentUrlMutation({ id: source.documentId });

        documentUrl = getBidDocumentUrl.documentUrl;
      } else {
        if (!urlShareSignature) {
          console.error("No signature found");
          return;
        }
        const resp = await getBidDocumentUrlForExternalUser(
          bidId,
          source.documentId,
          urlShareSignature,
        );
        documentUrl = resp.documentUrl;
      }

      setDocumentDrawerOpen(true);
      setDocumentData({
        ...source,
        documentLink: documentUrl,
      });
    },
    [bidId, getBidDocumentUrlMutation, urlShareSignature],
  );

  const onSelectDocument = useCallback(
    async (
      source: AiChatStreamResponse_Sources,
      trackingContext: EventNames,
      trackingData: EventData,
    ) => {
      if (source?.documentId === NOTICE_SOURCE) {
        handleNoticeLink();
      } else {
        await handleDocumentLink(source);
      }

      logEvent(trackingContext, {
        ...trackingData,
        "Record GUID": noticeId,
        "Opportunity ID": opportunityId,
      });
    },
    [handleDocumentLink, handleNoticeLink, logEvent, noticeId, opportunityId],
  );

  const hasEditAccess = !!window.currentUser && window.currentUser?.team.id === bidInfo?.teamId;

  if (isLoadingBidInfo || isQuerying || isQualifying) {
    return <BidReportLoadingState />;
  }

  // If there's no data and no bidInfo, there is no bid, so redirect the user
  const noBidData = !isLoadingBidInfo && !bidInfo && !isQuerying && !data;

  if (noBidData) {
    return <Redirect to="/" />;
  }

  return (
    <div className={css.qualificationReportPage}>
      <BidNoBidReportHeader
        bidId={bidId}
        bidInfo={bidInfo}
        data={data}
        urlShareSignature={urlShareSignature}
        slideoutOpen={slideoutOpen}
        setSlideoutOpen={setSlideoutOpen}
        onRegenerateBidReport={() =>
          onRegenerateBidReport(EventNames.qualificationReportRegenerated)
        }
      />
      <DetailsContent className={css.pageContent}>
        <div className={css.scoreCardWrapper}>
          <div className={css.noticeHeader}>
            <div className={css.titleContent}>
              <Text h1>Bid / no-bid report</Text>

              {/* For now, only allowing logged in users because we only have a graphql endpoint */}
              {!!window.currentUser && (
                <Button
                  buttonType="secondary"
                  onClick={() => {
                    logEvent(EventNames.stotlesAiOpened, {
                      "Bid ID": bidId,
                      "Record GUID": noticeId,
                      "Opportunity ID": opportunityId,
                    });
                    setIsAskAiModalOpen(true);
                  }}
                  iconProps={{ size: 16, color: "sysPrimaryDefault" }}
                  icon="stars2"
                  className={css.askAIBtn}
                  blackText
                >
                  Ask Stotles AI
                </Button>
              )}
            </div>
            {/* Not using wouter link because of an external user comes in here, we need to direct them to SEO - wouter links don't do that */}
            <div className={css.properties}>
              {noticeId && noticeTitle && (
                <span className={css.property}>
                  <span className={css.propTitle}>
                    <UIcon className={css.icon} icon="file06" color={"sysTextSecondary"} />
                    Notice title
                  </span>
                  <EllipsisTooltipText
                    fullText={noticeTitle}
                    containerClassname={css.linkTitle}
                    textProps={{
                      onClick: handleNoticeLink,
                    }}
                  />
                </span>
              )}
              {buyerName && buyerId && (
                <span className={css.property}>
                  <span className={css.propTitle}>
                    <UIcon className={css.icon} color="sysTextSecondary" icon="wallet02" />
                    Buyer
                  </span>
                  <EllipsisTooltipText
                    fullText={buyerName}
                    containerClassname={css.linkTitle}
                    textProps={{
                      // Open in a new tab
                      onClick: () => window.open(`${routes.buyer}/${buyerId}`, "_blank"),
                    }}
                  />
                </span>
              )}
            </div>
          </div>
          <p className={css.explainerText}>
            Expand and review the information below to make your bid / no-bid decision in minutes,
            not days.
          </p>
          <div className={css.qaReportContent}>
            {data?.map((section) => (
              <BidNoBidReportSection
                key={section.groupName}
                scoreCardResult={section}
                procurementStageId={procurementStageId ?? undefined}
                bidId={bidId}
                debugMode={debug}
                onSelectDocument={onSelectDocument}
                slideoutOpen={slideoutOpen}
                noticeTitle={noticeTitle}
                hasEditAccess={hasEditAccess}
              />
            ))}
          </div>
          <Flex gap="24px" flexDirection="column">
            <p className={css.explainerText}>
              *AI generated responses vary in accuracy - please verify important information.
            </p>
            {window.currentUser?.admin && (
              <Button
                as="button"
                buttonType="secondary"
                fullWidth={false}
                style={{ width: "fit-content" }}
                onClick={() => setDebug(!debug)}
              >
                Toggle debug
              </Button>
            )}
          </Flex>
        </div>
        {slideoutOpen && (
          <DocumentSlideout
            bidId={bidId}
            // Should have an opportunityId at this stage
            opportunityId={opportunityId ?? ""}
            hasEditAccess={hasEditAccess}
            onSelectDocument={(source) =>
              onSelectDocument(source, EventNames.tenderDocumentOpened, {
                "Action type": "Document reader opened",
                "Document name": source.document,
              })
            }
            onRegenerateBidReport={() =>
              onRegenerateBidReport(EventNames.additionalDocumentsRegenerated)
            }
          />
        )}

        {documentData && (
          <DocumentDrawer
            open={documentDrawerOpen}
            source={documentData}
            onClose={() => setDocumentDrawerOpen(false)}
          />
        )}
      </DetailsContent>
      <StotlesAiModal
        bidId={bidId}
        procurementStageId={procurementStageId ?? undefined}
        isOpen={isAskAiModalOpen && !useAiConversationStream}
        onClose={() => setIsAskAiModalOpen(false)}
        onSelectDocument={onSelectDocument}
        documentDrawerOpen={documentDrawerOpen}
        noticeTitle={noticeTitle}
      />
      {/* Open this modal if AI_CONVERSATION feature flag is on */}
      <AskStotlesAiModal
        resourceId={bidId}
        resourceName={opportunityName}
        noticeTitle={noticeTitle}
        isOpen={isAskAiModalOpen && useAiConversationStream}
        onClose={() => setIsAskAiModalOpen(false)}
        onSelectDocument={onSelectDocument}
        // TODO: Fix layering
        // documentDrawerOpen={documentDrawerOpen}
      />
    </div>
  );
}
